import { action, makeObservable, computed } from 'mobx';
import { getRoot } from 'mobx-easy';
import { hasFeatureFlag } from '@bringg-frontend/global-stores';

import FilterStore from '../filters/filters-store';
import { FILTERS_DEFAULT_VALUES } from 'bringg-web/features/filter-bar/filter-bar';
import {
	getActivePresetViewForView,
	getPresetViewType,
	PRESETS_VIEWS_FEATURE_FLAG
} from 'bringg-web/features/preset-views/utils';
import type RootStore from 'bringg-web/stores/root-store';

// dup type
export type ViewNames = 'PlanningView' | 'DispatchView';

export default class FilterStoreAdapter extends FilterStore {
	constructor(viewName: ViewNames) {
		super(viewName);

		makeObservable(this, {
			resetAllFilters: action,
			updateFilterList: action,
			getFiltersList: computed
		});
	}

	private get getPresetViewFilters() {
		return this.getActivePresetView()?.filters || FILTERS_DEFAULT_VALUES;
	}

	private updatePresetViewFilters = (updateSections: Record<string, unknown>) => {
		const activePresetView = this.getActivePresetView();

		const newValues = {
			...activePresetView?.filters,
			...updateSections,
			...{ availableCustomAttributesFilterTypes: this.availableCustomAttributesFilterTypes }
		};

		activePresetView?.updateFilters(newValues);
	};

	private resetPresetViewFilters = () => {
		this.getActivePresetView()?.updateFilters(FILTERS_DEFAULT_VALUES);
	};

	getActivePresetView = () => {
		return getActivePresetViewForView(getPresetViewType(this.activeView));
	};

	replaceFilterList = (newList, setTouched = true) => {
		if (this.isPresetViewsEnabled) {
			this.getActivePresetView()?.updateFilters(newList, setTouched);
		} else {
			this.replaceFilterListNonPreset(newList);
		}
	};

	resetAllFilters = () => {
		if (this.isPresetViewsEnabled) {
			this.resetPresetViewFilters();
		} else {
			this.resetAllFiltersNonPreset();
		}
	};

	updateFilterList = updateSections => {
		if (this.isPresetViewsEnabled) {
			this.updatePresetViewFilters(updateSections);
		} else {
			this.updateFilterListNonPreset(updateSections);
		}
	};

	get getFiltersList() {
		if (this.isPresetViewsEnabled) {
			return this.getPresetViewFilters;
		} else {
			return this.getFiltersListNonPreset;
		}
	}

	get isPresetViewsEnabled() {
		const { usersStore } = getRoot<RootStore>().data;
		return hasFeatureFlag(usersStore.currentUser, PRESETS_VIEWS_FEATURE_FLAG);
	}
}
